/* ============================================================
   The Corridor Run

   One consignment, every consequence. Cost against the published band,
   the margin on the trip, duty payable on arrival, demurrage accruing
   at the terminal, carbon for the tender, and what the whole thing does
   to cash — from a single input set.

   The stat tiles are not the point. The findings are: an operator can
   already get four correct answers from four tools and still miss that
   the demurrage has quietly overtaken the margin, because no tool was
   holding both numbers at once. That interaction is what this page
   exists to surface, so it leads and the tiles support it.

   Everything here composes engines that already refuse to guess. A
   figure not supplied is reported as not supplied and any finding
   depending on it is withheld — see the note at the top of
   data/corridor-run.js.
   ============================================================ */

import { assessRun } from '../../data/corridor-run.js';
import { getAllCorridors, getBenchmark, availableModes } from '../../data/benchmarks.js';
import { DUTY_BANDS } from '../../data/duty.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { money, number, percent, parseNumber } from '../../shared/format.js';
import { saveRecord } from '../../shared/records.js';
import { prefillForm, rememberProfile } from '../../shared/profile.js';
import { PrefillNotice } from '../../components/PrefillNotice.js';
import { PrintButton } from '../../components/PrintButton.js';
import { ShareButton } from '../../components/ShareButton.js';
import { BoardPreamble, boardFootnote } from '../../components/BoardPreamble.js';
import { readShared, SHARE_PARAM } from '../../shared/share.js';

const OPERATOR_TYPES = [
  { id: 'transporter', label: 'Transporter / haulier' },
  { id: 'clearing-agent', label: 'Clearing & forwarding agent' },
  { id: 'importer', label: 'Importer' },
  { id: 'warehouse', label: 'Warehouse / CFS operator' }
];

const SEVERITY = {
  danger: { tone: 'var(--ca-danger)', glyph: 'alert' },
  warning: { tone: 'var(--ca-warning)', glyph: 'alert' },
  info: { tone: 'var(--ca-info)', glyph: 'info' }
};

export function CorridorRunPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>The Corridor Run</h1>
      <p>
        One consignment, every consequence — cost, margin, duty, demurrage, carbon and cash,
        from one set of numbers. The tools each answer well on their own; the money is usually
        lost between them.
      </p>
    </header>

    <div id="cr-form">
      <section class="section">
        <h2 class="section-title">The run</h2>
        <div class="card">
          <label class="field">
            <span class="field-label">Corridor</span>
            <select id="cr-corridor" class="input-field"></select>
          </label>
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Mode</span>
              <select id="cr-mode" class="input-field"></select>
            </label>
            <label class="field">
              <span class="field-label">Distance, one way (km)</span>
              <input id="cr-distance" class="input-field num" type="number" inputmode="numeric"
                     min="1" step="1" placeholder="485" />
            </label>
          </div>
          <div class="grid-2" style="margin-bottom:0;">
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Payload (tonnes)</span>
              <input id="cr-payload" class="input-field num" type="number" inputmode="decimal"
                     min="0.1" step="0.1" placeholder="26" />
            </label>
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Containers</span>
              <input id="cr-containers" class="input-field num" type="number" inputmode="numeric"
                     min="1" step="1" value="1" />
            </label>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Money on this run</h2>
        <div class="card">
          <div class="grid-2" style="margin-bottom:0;">
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Revenue for the run (USD)</span>
              <input id="cr-revenue" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="10" placeholder="1400" />
            </label>
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Your cost to run it (USD)</span>
              <input id="cr-cost" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="10" placeholder="1120" />
              <span class="field-hint">Fuel, driver, tolls, maintenance — everything variable.</span>
            </label>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">At the border and the terminal</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Customs value, CIF (USD)</span>
              <input id="cr-cif" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="100" placeholder="45000" />
              <span class="field-hint">Leave blank if you are not the one paying duty.</span>
            </label>
            <label class="field">
              <span class="field-label">Import duty band</span>
              <select id="cr-band" class="input-field">
                ${DUTY_BANDS.map((b) => html`<option value="${b.id}">${b.label}</option>`)}
              </select>
            </label>
          </div>
          <div class="grid-2" style="margin-bottom:0;">
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Free days</span>
              <input id="cr-free-days" class="input-field num" type="number" inputmode="numeric"
                     min="0" step="1" value="4" />
            </label>
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Days held</span>
              <input id="cr-days-held" class="input-field num" type="number" inputmode="numeric"
                     min="0" step="1" placeholder="9" />
            </label>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">You, and how you get paid</h2>
        <div class="card">
          <div class="grid-2" style="margin-bottom:0;">
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">You are a…</span>
              <select id="cr-operator" class="input-field">
                <option value="">Not specified</option>
                ${OPERATOR_TYPES.map((o) => html`<option value="${o.id}">${o.label}</option>`)}
              </select>
            </label>
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Debtor days (DSO)</span>
              <input id="cr-dso" class="input-field num" type="number" inputmode="numeric"
                     min="0" step="1" placeholder="60" />
              <span class="field-hint">Invoice to payment received.</span>
            </label>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Optional — upgrades the carbon figure</h2>
        <div class="card">
          <label class="field" style="margin-bottom:0;">
            <span class="field-label">Fuel burnt on this run (litres)</span>
            <input id="cr-fuel" class="input-field num" type="number" inputmode="decimal"
                   min="0" step="1" placeholder="180" />
            <span class="field-hint">
              Measured fuel makes the carbon figure primary data under ISO 14083 rather than a
              framework default a shipper will discount.
            </span>
          </label>
        </div>
      </section>

      <div id="cr-error"></div>

      <button class="btn btn-primary btn-block" id="cr-calculate" type="button"
              style="padding: var(--ca-space-4);">
        Assess this run
      </button>
    </div>

    <div id="cr-results" hidden></div>
  `;

  const corridorSelect = page.querySelector('#cr-corridor');
  const modeSelect = page.querySelector('#cr-mode');
  const distanceInput = page.querySelector('#cr-distance');

  for (const c of getAllCorridors()) {
    const option = document.createElement('option');
    option.value = c.key;
    option.textContent = `${c.name} · ${c.distance} km`;
    corridorSelect.appendChild(option);
  }

  const refreshModes = () => {
    const modes = availableModes(corridorSelect.value);
    const previous = modeSelect.value;
    modeSelect.replaceChildren();
    for (const m of modes) {
      const option = document.createElement('option');
      option.value = m;
      option.textContent = m === 'rail' ? 'Rail (SGR)' : 'Road';
      modeSelect.appendChild(option);
    }
    if (modes.includes(previous)) modeSelect.value = previous;
  };

  corridorSelect.addEventListener('change', () => {
    const bench = getBenchmark(corridorSelect.value);
    if (bench) distanceInput.value = String(bench.distance);
    refreshModes();
  });

  const bench = getBenchmark(corridorSelect.value);
  if (bench) distanceInput.value = String(bench.distance);
  refreshModes();

  const applied = prefillForm(page, {
    corridor: 'cr-corridor',
    mode: 'cr-mode',
    payload: 'cr-payload',
    containers: 'cr-containers',
    freeDays: 'cr-free-days',
    dso: 'cr-dso'
  });
  if (applied.length > 0) {
    refreshModes();
    const notice = PrefillNotice(applied, page);
    if (notice) page.querySelector('#cr-form').prepend(notice);
  }

  page.querySelector('#cr-calculate').addEventListener('click', () => calculate(page));

  /* A shared link, if this is one. It is read AFTER the profile prefill
     and overwrites it: someone who followed a link came to look at that
     run, not at their own last one, and a form half in each would be a
     third run nobody performed.

     decodeRun returns null for anything it cannot read completely. A
     link that fails is not silently ignored — the reader is told, and
     left with a blank form rather than a partly filled one that looks
     deliberate. */
  const shared = readShared();
  if (shared) {
    applyShared(page, shared.input, refreshModes);
    page.querySelector('#cr-form').prepend(sharedNotice(shared.fields));
    // Run it. The recipient opened a link to see an answer, and a filled
    // form with an unpressed button is a step they did not ask for.
    calculate(page);
  } else if (new URLSearchParams(window.location.search).has(SHARE_PARAM)) {
    /* A substring test for "s=" would fire on "?results=1". The
       parameter is either there or it is not. */
    page.querySelector('#cr-form').prepend(brokenLinkNotice());
  }

  return page;
}

/* One line per field the link carries, in the same order share.js
   encodes them, so the two lists can be read side by side. A field
   added there and forgotten here would arrive in the URL and never
   reach a box; keeping the order identical is what makes that visible
   rather than silent. */
function applyShared(page, input, refreshModes) {
  const set = (id, value) => {
    const el = page.querySelector(`#${id}`);
    if (el && value !== undefined && value !== null) el.value = String(value);
  };

  set('cr-corridor', input.corridorKey);
  refreshModes();
  set('cr-mode', input.mode);
  set('cr-distance', input.distance);
  set('cr-payload', input.payload);
  set('cr-containers', input.containers);
  set('cr-revenue', input.tripRevenue);
  set('cr-cost', input.tripCost);
  set('cr-cif', input.cif);
  set('cr-band', input.dutyBandId);
  set('cr-free-days', input.freeDays);
  set('cr-days-held', input.daysHeld);
  set('cr-dso', input.dso);
  set('cr-fuel', input.fuelLitres);
  set('cr-operator', input.operatorType);
}

function sharedNotice(fields) {
  const node = document.createElement('div');
  node.className = 'notice notice-info no-print';
  node.style.marginBottom = 'var(--ca-space-4)';
  node.innerHTML = html`
    <span style="flex-shrink:0;margin-top:2px;">${raw(icon('link', 15))}</span>
    <div style="font-size: var(--ca-text-xs); flex:1;">
      <strong style="font-weight: var(--ca-weight-medium);">Opened from a shared link</strong> —
      ${fields} figure${fields === 1 ? '' : 's'} came from whoever sent it, and the result below
      was computed here, on this device, from those figures. Nothing was carried over from the
      sender's own screen. Change anything you would price differently.
    </div>
  `;
  return node;
}

function brokenLinkNotice() {
  const node = document.createElement('div');
  node.className = 'notice notice-warning no-print';
  node.style.marginBottom = 'var(--ca-space-4)';
  node.innerHTML = html`
    <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
    <div style="font-size: var(--ca-text-xs); flex:1;">
      <strong style="font-weight: var(--ca-weight-medium);">That link could not be read</strong> —
      it may have been truncated in a message, or written by a newer version of this tool. The
      form has been left blank rather than partly filled, because half a run looks deliberate
      and is not. Ask the sender to send it again.
    </div>
  `;
  return node;
}

function calculate(page) {
  const val = (id) => parseNumber(page.querySelector(`#${id}`).value);
  const raw_ = (id) => page.querySelector(`#${id}`).value;

  const input = {
    corridorKey: raw_('cr-corridor'),
    mode: raw_('cr-mode'),
    distance: val('cr-distance'),
    payload: val('cr-payload'),
    containers: val('cr-containers') ?? 1,
    tripRevenue: val('cr-revenue'),
    tripCost: val('cr-cost'),
    cif: val('cr-cif'),
    dutyBandId: raw_('cr-band'),
    freeDays: val('cr-free-days') ?? 0,
    daysHeld: val('cr-days-held') ?? 0,
    dso: val('cr-dso'),
    fuelLitres: val('cr-fuel'),
    operatorType: raw_('cr-operator') || null
  };

  const errors = [];
  if (!(input.distance > 0)) errors.push('Enter a distance greater than zero.');
  if (!(input.payload > 0)) errors.push('Enter a payload greater than zero.');
  if (!(input.tripCost > 0)) errors.push('Enter what the run costs you.');
  if (input.containers < 1) errors.push('There must be at least one container.');
  // The engine clamps these, but silently correcting a typo is not the
  // same as telling someone about it.
  if (input.freeDays < 0) errors.push('Free days cannot be negative.');
  if (input.daysHeld < 0) errors.push('Days held cannot be negative.');
  if (input.cif !== null && input.cif < 0) errors.push('A customs value cannot be negative.');
  if (input.dso !== null && input.dso < 0) errors.push('Debtor days cannot be negative.');

  const errorSlot = page.querySelector('#cr-error');
  if (errors.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">Cannot assess this run yet</strong>
          <ul style="margin: var(--ca-space-1) 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${errors.map((e) => html`<li>${e}</li>`)}
          </ul>
        </div>
      </div>
    `;
    errorSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  errorSlot.innerHTML = '';

  const result = assessRun(input);
  render(page, result);

  /* Two audiences, one record.

     The flat fields are what /saved lists — a one-line summary of a run,
     which is all that page ever wanted.

     `result` is what /compare needs, and it is the whole assessed run
     rather than a second set of summary fields, because a comparison
     asks questions the summary cannot answer: what payload was this
     priced at (the caveat), what model computed it (the caveat), what
     was the margin and the carbon intensity. Deriving those from four
     flat numbers is not possible, and adding four more would only push
     the same problem to the next metric.

     `obligations` is deliberately dropped. It is the one part of a run
     that is not a property of the run: it is a snapshot of what the law
     said on the day the button was pressed, and freezing that into
     storage would let a saved run assert a regulatory position the
     engine has since moved on from. Everything else here is arithmetic
     on figures the operator typed, and stays true. This is the same
     rule share links follow — carry what was entered, never a computed
     claim that can go stale underneath the reader. */
  const { obligations: _obligations, ...storable } = result;

  void saveRecord('corridor-run', {
    corridor: input.corridorKey,
    costPerTonKm: result.cost.costPerTonKm,
    profit: result.margin?.profit ?? null,
    demurrage: result.demurrage.total,
    findings: result.findings.length,
    result: storable
  });

  rememberProfile({
    corridor: input.corridorKey,
    mode: input.mode,
    distance: input.distance,
    payload: input.payload,
    containers: input.containers,
    freeDays: input.freeDays,
    dso: input.dso
  });
}

function render(page, r) {
  const results = page.querySelector('#cr-results');
  results.hidden = false;
  results.replaceChildren();

  /* ---- The findings lead. They are why this page exists. ---- */
  if (r.findings.length > 0) {
    const section = document.createElement('section');
    section.className = 'section';
    section.innerHTML = html`<h2 class="section-title">What this run is actually doing</h2>`;

    for (const f of r.findings) {
      const meta = SEVERITY[f.severity] || SEVERITY.info;
      const card = document.createElement('div');
      card.className = 'card';
      card.style.marginBottom = 'var(--ca-space-3)';
      card.style.borderLeft = `3px solid ${meta.tone}`;
      card.innerHTML = html`
        <div class="row" style="align-items: flex-start; gap: var(--ca-space-3);">
          <span style="flex-shrink:0; margin-top:2px; color:${meta.tone};">
            ${raw(icon(meta.glyph, 16))}
          </span>
          <div style="flex:1; min-width:0;">
            <div style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);
                        color:${meta.tone}; margin-bottom: 3px;">
              ${f.title}
            </div>
            <div style="font-size: var(--ca-text-xs); line-height: var(--ca-leading-normal);
                        color: var(--ca-text-secondary);">
              ${f.detail}
            </div>
          </div>
        </div>
      `;
      section.appendChild(card);
    }
    results.appendChild(section);
  }

  /* ---- The numbers behind them ---- */
  const tiles = document.createElement('section');
  tiles.className = 'section';
  tiles.innerHTML = html`
    <h2 class="section-title">The run, by the numbers</h2>
    <div class="card">
      <div class="grid-2">
      ${statTile(
        'Cost per tonne-km',
        r.cost.costPerTonKm ? `$${r.cost.costPerTonKm.toFixed(3)}` : '—',
        r.cost.comparison?.available
          ? `Band $${r.cost.comparison.low.toFixed(3)}–$${r.cost.comparison.high.toFixed(3)}`
          : r.cost.comparison?.reason || 'No published band',
        r.cost.comparison?.tone
      )}
      ${statTile(
        'Margin on the run',
        r.margin ? money(r.margin.profit) : '—',
        r.margin ? `${percent(r.margin.marginPct, 1)} of revenue` : 'Enter the revenue',
        r.margin ? (r.margin.profit > 0 ? 'var(--ca-positive)' : 'var(--ca-danger)') : null
      )}
      ${statTile(
        'Duty on arrival',
        r.duty ? money(r.duty.totalTax) : '—',
        r.duty ? 'Payable to KRA before release' : 'Enter a customs value',
        null
      )}
      ${statTile(
        'Demurrage accrued',
        money(r.demurrage.total),
        r.demurrage.total > 0
          ? `Next day adds ${money(r.nextDay)}`
          : `${r.demurrage.daysRemaining} free days left`,
        r.demurrage.total > 0 ? 'var(--ca-warning)' : 'var(--ca-positive)'
      )}
      ${statTile(
        'Carbon intensity',
        r.carbon ? `${number(r.carbon.intensity, 1)}` : '—',
        r.carbon ? `gCO₂e per tonne-km · ${r.carbon.quality} data` : 'Enter distance and payload',
        null
      )}
      ${statTile(
        'Duty out of the business',
        r.cash ? `${number(r.cash.dutyFloatDays)} days` : '—',
        r.cash ? `${money(r.cash.lockedUp)} tied up` : 'Enter debtor days and a customs value',
        r.cash && r.cash.dutyFloatDays > 60 ? 'var(--ca-warning)' : null
      )}
      </div>
    </div>
  `;
  results.appendChild(tiles);

  /* ---- What breaks first ---- */
  if (r.breakingPoints.length > 0) {
    const section = document.createElement('section');
    section.className = 'section';
    section.innerHTML = html`
      <h2 class="section-title">What breaks this run first</h2>
      <div class="card">
        <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);
                  margin-bottom: var(--ca-space-4);">
          The move required to take this run to zero, one lever at a time, smallest move first.
          Solved from the figures above rather than sampled, so these are the crossing points and
          not the resolution of a grid.
        </p>
        <div class="stack" style="gap: var(--ca-space-3);">
          ${r.breakingPoints.map((b) => {
            const meta = SEVERITY[b.severity] || SEVERITY.info;
            return html`
              <div class="row" style="align-items: flex-start; gap: var(--ca-space-3);">
                <span
                  class="badge num"
                  style="flex-shrink:0; min-width: 92px; justify-content: center;
                         background: color-mix(in srgb, ${meta.tone} 13%, transparent);
                         color:${meta.tone};"
                >
                  ${b.atLabel}
                </span>
                <div style="flex:1; min-width:0;">
                  <div style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">
                    ${b.lever}
                  </div>
                  <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);
                              line-height: var(--ca-leading-normal);">
                    ${b.detail}
                  </div>
                </div>
              </div>
            `;
          })}
        </div>
      </div>
    `;
    results.appendChild(section);
  }

  /* ---- What is missing, said plainly rather than defaulted ---- */
  if (r.missing.length > 0) {
    const note = document.createElement('div');
    note.className = 'notice notice-info';
    note.style.marginBottom = 'var(--ca-space-4)';
    note.innerHTML = html`
      <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
      <div style="font-size: var(--ca-text-xs);">
        <strong style="font-weight: var(--ca-weight-medium);">Not supplied:</strong>
        ${r.missing.join(', ')}. Nothing has been assumed in their place — the findings that
        depend on them are simply not shown.
      </div>
    `;
    results.appendChild(note);
  }

  /* ---- What applies to you ---- */
  if (r.obligations.length > 0) {
    const section = document.createElement('section');
    section.className = 'section';
    section.innerHTML = html`
      <h2 class="section-title">In force for you on this run (${r.obligations.length})</h2>
      <div class="card">
        <div class="stack" style="gap: var(--ca-space-2);">
          ${r.obligations.map(
            (o) => html`
              <div style="font-size: var(--ca-text-xs);">
                <span style="font-weight: var(--ca-weight-medium);">${o.name}</span>
                <span style="color: var(--ca-text-quaternary);"> · ${o.authority}</span>
              </div>
            `
          )}
        </div>
      </div>
    `;
    results.appendChild(section);
  }

  results.appendChild(
    PrintButton({
      targetId: 'cr-results',
      title: 'Corridor Run Assessment',
      hintText:
        'Adds the assumptions, the basis of the benchmark and a document reference — the ' +
        'three things a reader who was not here will ask for first.',
      subtitle:
        `${r.cost.benchmark?.name || 'Corridor'} · ${r.input.mode} · ` +
        `${r.findings.length} finding${r.findings.length === 1 ? '' : 's'}`,
      buildPreamble: () => BoardPreamble(r),
      footnote: boardFootnote(r)
    })
  );

  /* Below the PDF, not beside it. The export is the primary action —
     it is what goes to a lender or a board — and the link is the
     lighter-weight thing you do when the answer only has to reach a
     colleague. `r.input` rather than the form: what was assessed, which
     is not necessarily what the boxes currently say. */
  results.appendChild(ShareButton({ path: '/corridor-run', input: r.input }));

  results.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function statTile(label, figure, caption, tone) {
  return html`
    <div style="padding-block: var(--ca-space-2);">
      <div style="font-size: var(--ca-text-2xs); text-transform: uppercase; letter-spacing: 0.1em;
                  color: var(--ca-text-quaternary); margin-bottom: var(--ca-space-1);">
        ${label}
      </div>
      <div class="stat-figure num" style="font-size: var(--ca-text-lg);${tone ? ` color:${tone};` : ''}">
        ${figure}
      </div>
      <div style="font-size: var(--ca-text-2xs); color: var(--ca-text-tertiary); margin-top: 2px;">
        ${caption}
      </div>
    </div>
  `;
}
